import type { CompiledSurvey, SurveyRuntimeContext } from '@apruv/survey-contract/compiler';
import type { FlowValues } from '../types';
import { canonicalComponentConfig, visiblePresentationComponents } from './surveyPresentation';

/** Validate the active feedback collection before persistence or navigation. */
export function prepareCanonicalFeedback(
  compiled: CompiledSurvey,
  screenId: string,
  context: SurveyRuntimeContext,
  values: FlowValues,
  skip: boolean,
  acknowledgedAnswers: FlowValues = {}
): FlowValues {
  const components = visiblePresentationComponents(compiled, screenId, context).filter((item) =>
    ['tag_selector', 'comment_box'].includes(item.type)
  );
  if (!components.length) throw new Error('This feedback step is no longer available.');
  const feedback = compiled.snapshot.configuration.feedback;
  if (skip && feedback.required) throw new Error('Please provide feedback before continuing.');
  const next = { ...values };
  let hasAnswer = false;
  for (const component of components) {
    const value = values[component.id];
    const answer =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as { submitted?: boolean; notes?: unknown; tags?: unknown })
        : {};
    if (skip) {
      // A bypass discards only this screen's unsaved draft, never an acknowledged answer.
      if (Object.prototype.hasOwnProperty.call(acknowledgedAnswers, component.id))
        next[component.id] = structuredClone(acknowledgedAnswers[component.id]);
      else if (answer.submitted !== true) delete next[component.id];
      continue;
    }
    if (component.type === 'comment_box') {
      const notes = typeof answer.notes === 'string' ? answer.notes : '';
      const length = notes.trim().length;
      if (length > feedback.maxLength)
        throw new Error(`Keep feedback within ${feedback.maxLength} characters.`);
      if (length > 0 && length < feedback.minLength)
        throw new Error(`Please enter at least ${feedback.minLength} characters.`);
      hasAnswer ||= length > 0;
    } else {
      const tags = Array.isArray(answer.tags) ? answer.tags : [];
      const config = canonicalComponentConfig(compiled, component, context);
      const allowed = feedback.reasons.length
        ? feedback.reasons.map((reason) => reason.id)
        : Array.isArray(config?.options)
          ? config.options
          : [];
      if (tags.some((tag) => typeof tag !== 'string' || !allowed.includes(tag)))
        throw new Error('Choose an available feedback reason.');
      if (feedback.selection === 'single' && tags.length > 1)
        throw new Error('Choose one feedback reason.');
      hasAnswer ||= tags.length > 0;
    }
  }
  if (!skip && feedback.required && !hasAnswer)
    throw new Error('Please provide feedback before continuing.');
  return next;
}
